import React, { useState } from "react";
import EmptyState from "../EmptyState";
import { HTML_INPUT_TYPE_KEY, INPUT_TYPE, SECTION_TYPE } from "./constants";
import styled from "styled-components";
import InputBox from "../InputBox";
import Dropdown from "../Dropdown";
import DatePicker from "react-datepicker";
import { createValidationSchema, validateForm } from "./validation";
import Button from "../Button";
import SuccessButton from "../SuccessButton";

const Container = styled.div`
	height: max-content;
	width: max-content;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	gap: 2rem;
	padding: 2rem;
`;

const CenteredRow = styled.div`
	width: max-content;
	height: auto;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 2rem;
`;

const Block = styled.div`
	width: max-content;
	height: auto;
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.gap || "0.2rem"};
`;

const Label = styled.label`
	font-size: 16px;
	height: 22px;
	min-width: 76px;
`;

const Popper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
	display: flex;
	flex-direction: row;
`;

const EndRow = styled.div`
	width: 100%;
	height: auto;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	flex-wrap: wrap;
	gap: 1.5rem;
`;

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: max-content;
	height: max-content;
`;

const ComponentErrorMessage = styled.div`
	margin-top: 5px;
	text-align: center;
	color: red;
	font-size: 12px;
    height: 12px;
`;


function handleOnChange({ setFieldData, clearFieldError, mappedErrors, name, event, value, type }) {
	if (mappedErrors[name]) clearFieldError(name);

	switch (type) {
		case INPUT_TYPE.TIME:
		case INPUT_TYPE.TEXT:
			setFieldData(name, event.target.value);
			break;

		case INPUT_TYPE.NUMBER:
			setFieldData(name, event.target.valueAsNumber ?? event.target.value);
			break;

		default:
			setFieldData(name, value);
			break;
	}
}

function parser({ config, formData, setFieldData, mappedErrors, clearFieldError }) {
	if (!config) {
		return <EmptyState message="No Config Provided" />;
	}

	let renderData = [];

	for (const section in config) {
		let sectionData = [];

		const { fields } = config[section];
		
		for (const field of fields) {
			const { required, type, name, label, defaultValue, componentProps, options } = field;

            let errored = false, errorMessage = "";

            if(mappedErrors[name]) {
                errored = true;
                errorMessage = mappedErrors[name]
            }

			switch (type) {
				case INPUT_TYPE.TEXT:
				case INPUT_TYPE.NUMBER:
				case INPUT_TYPE.TIME:

					sectionData.push(
						<Block>
							<Label>{label || name}</Label>
							<InputBox
                                errored={errored}
                                errorMessage={errorMessage}
								{...componentProps}
								onChange={(event) => handleOnChange({ event, setFieldData, mappedErrors, clearFieldError, type, name })}
								type={HTML_INPUT_TYPE_KEY[type]}
								defaultValue={defaultValue}
								value={formData[name]}
								required={required}
							/>
						</Block>
					);
					break;

				case INPUT_TYPE.SELECT:
					sectionData.push(
						<Block>
							<Label>{label || name}</Label>
							<Dropdown
                                errored={errored}
                                errorMessage={errorMessage}
								{...componentProps}
								width
								optionsList={options}
								onChange={(value) => handleOnChange({ setFieldData, mappedErrors, clearFieldError, type, name, value })}
								defaultValue={defaultValue}
								value={formData[name]}
								required={required}
							/>
						</Block>
					);
					break;

				case INPUT_TYPE.DATES:
					sectionData.push(
						<Block>
							<Label>{label || name}</Label>
                            <Wrapper>
                                <DatePicker
                                    className={errored ? "errored" : ""}
                                    selectedDates={formData[name]}
                                    selectsMultiple
                                    popperContainer={Popper}
                                    onChange={(value) => {
                                        handleOnChange({ setFieldData, mappedErrors, clearFieldError, type, name, value });
                                    }}
                                    // includeDates={validDates}
                                    dateFormat={"MM/dd/yyyy"}
                                    shouldCloseOnSelect={false}
									{...componentProps}
                                />
                                {errored && errorMessage ? <ComponentErrorMessage>{errorMessage}</ComponentErrorMessage> : ""}
                            </Wrapper>
						</Block>
					);
					break;

				default:
					break;
			}
		}

		renderData.push(<CenteredRow>{sectionData}</CenteredRow>);
	}
	return renderData;
}

const Form = ({ title = "Form", onSubmit, onCancel, config, submitButtonLoading, canCancel = false, submitButtonText = "Submit" }) => {
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});

	function handleOnSubmit(config, formData) {
		const validators = createValidationSchema(config);
		const { ok, mappedErrors } = validateForm(validators, formData);
		if (!ok && mappedErrors) {
			setErrors(mappedErrors);
			return;
		}

		onSubmit(formData);
	}

	function handleOnCancel() {
		onCancel();
	}

	function setFieldData(name, value) {
		setFormData((prev) => ({ ...prev, [name]: value }));
	}

	function clearFieldError(name) {
		setErrors((prev) => { delete prev[name]; return prev; });
	}

	return (
		<Container>
			<h2>{title}</h2>
			<>{parser({ clearFieldError, setFieldData, mappedErrors: errors, formData, config })}</>
			<EndRow>
				{ canCancel ? <Button onClick={handleOnCancel}>Cancel</Button> : "" }
				<SuccessButton disabled={submitButtonLoading} onClick={() => handleOnSubmit(config, formData)}>{submitButtonText}</SuccessButton>
			</EndRow>
		</Container>
	);
};

export default Form;
